import {useCallback, useEffect, useState} from 'react';

import logError, {logBreadcrumb} from '@/utils/errors/logError';

const APPLE_PAY_VERSION = 3;
const MERCHANT_NAME = 'Modak';
const CURRENCY = 'USD';
const COUNTRY_CODE = 'US';
const SUPPORTED_NETWORKS: string[] = ['visa', 'masterCard', 'amex', 'discover'];

export type ApplePaySessionHandlers = {
  validateMerchant: () => Promise<unknown>;
  submitPayment: (
    token: ApplePayJS.ApplePayPaymentToken,
    billingContact: ApplePayJS.ApplePayPaymentContact,
    shippingContact?: ApplePayJS.ApplePayPaymentContact,
  ) => Promise<{id: string; status: string}>;
  onSuccess: (paymentId: string) => void;
  onError: () => void;
};

export function useApplePaySession(
  amountInCents: number,
  handlers: ApplePaySessionHandlers,
  applePayApiEnabled: boolean,
) {
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    if (
      typeof ApplePaySession !== 'undefined' &&
      window.location.protocol === 'https:' &&
      ApplePaySession.canMakePayments() &&
      applePayApiEnabled
    ) {
      setIsAvailable(true);
    } else {
      setIsAvailable(false);
    }
  }, [applePayApiEnabled]);

  const startSession = useCallback(() => {
    if (typeof ApplePaySession === 'undefined') {
      handlers.onError();
      return;
    }

    const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
      countryCode: COUNTRY_CODE,
      currencyCode: CURRENCY,
      supportedNetworks: SUPPORTED_NETWORKS,
      merchantCapabilities: ['supports3DS'],
      total: {
        label: MERCHANT_NAME,
        amount: (amountInCents / 100).toFixed(2),
      },
      requiredBillingContactFields: ['postalAddress', 'name', 'email', 'phone'],
      // email/phone arrive in shippingContact, NOT billingContact:
      requiredShippingContactFields: ['email', 'phone'],
    };

    let session: ApplePaySession;
    try {
      session = new ApplePaySession(APPLE_PAY_VERSION, paymentRequest);
    } catch (error) {
      logError(error, {
        fileName: 'useApplePaySession.ts',
        functionName: 'startSession',
        customTitle: 'ApplePay - session creation failed',
      });
      handlers.onError();
      return;
    }

    session.onvalidatemerchant = async (
      event: ApplePayJS.ApplePayValidateMerchantEvent,
    ) => {
      logBreadcrumb({
        category: 'ApplePay.useApplePaySession.onvalidatemerchant',
        type: 'query',
        message: 'Merchant validation started',
        data: {validationUrl: event.validationURL},
      });
      try {
        const merchantSession = await handlers.validateMerchant();
        session.completeMerchantValidation(merchantSession);
      } catch (error) {
        logError(error, {
          fileName: 'useApplePaySession.ts',
          functionName: 'onvalidatemerchant',
          customTitle: 'ApplePay - merchant validation failed',
        });
        session.abort();
        handlers.onError();
      }
    };

    session.onpaymentauthorized = async (
      event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    ) => {
      const {token, billingContact, shippingContact} = event.payment;
      logBreadcrumb({
        category: 'ApplePay.useApplePaySession.onpaymentauthorized',
        type: 'user',
        message: 'Payment authorized by user',
      });
      try {
        const response = await handlers.submitPayment(
          token,
          billingContact,
          shippingContact,
        );

        if (response.status === 'Authorized') {
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          handlers.onSuccess(response.id);
        } else {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          handlers.onError();
        }
      } catch (error) {
        logError(error, {
          fileName: 'useApplePaySession.ts',
          functionName: 'onpaymentauthorized',
          customTitle: 'ApplePay - payment authorization failed',
        });
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        handlers.onError();
      }
    };

    session.oncancel = () => {
      logBreadcrumb({
        category: 'ApplePay.useApplePaySession.oncancel',
        type: 'user',
        message: 'User cancelled Apple Pay sheet',
      });
    };

    session.begin();
  }, [amountInCents, handlers]);

  return {isAvailable, startSession};
}
